<template>
  <div>
    <!-- ============================================================== -->
    <!-- Bread crumb and right sidebar toggle -->
    <!-- ============================================================== -->
    <div class="page-breadcrumb border-bottom">
      <div class="row">
        <div class="col-lg-3 col-md-4 col-xs-12 align-self-center">
          <h5 class="font-medium text-uppercase mb-0">Stripe Price Sync</h5>
        </div>
        <div class="col-lg-9 col-md-8 col-xs-12 align-self-center">
          <nav aria-label="breadcrumb" class="float-left mt-2 float-md-right">
            <ol class="p-0 mb-0 breadcrumb justify-content-end">
              <li class="breadcrumb-item">
                <a>Home</a>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                Stripe Price Sync
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
    <!-- ============================================================== -->
    <!-- End Bread crumb and right sidebar toggle -->
    <!-- ============================================================== -->
    <!-- ============================================================== -->
    <!-- Container fluid  -->
    <!-- ============================================================== -->
    <div class="page-content container-fluid">
      <!-- ============================================================== -->
      <!-- Start Page Content -->
      <!-- ============================================================== -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="row">
                <div class="col-md-8">
                  <h4 class="card-title">Stripe → Postgres Mirror Sync</h4>
                  <p class="card-text">
                    Pull the latest Stripe products, prices and features into the AhaSlides
                    Postgres mirror tables — the same job as the manual
                    <code>cli/stripe-product-and-price-sync.js</code> GitHub workflow, on demand.
                  </p>

                  <div v-if="lastResult" class="alert alert-success">
                    <i class="fa fa-check-circle mr-1" aria-hidden="true"></i>
                    <strong>Last sync:</strong> synced
                    {{ lastResult.productsN }} products and {{ lastResult.pricesN }} prices
                    <span v-if="lastSyncTime">at {{ lastSyncTime }}</span>.
                  </div>
                </div>
                <div class="col-md-4 text-center">
                  <button
                    class="btn btn-primary btn-lg"
                    :disabled="isLoading"
                    @click="triggerStripeSync"
                  >
                    <i
                      class="fa fa-sync fa-fw"
                      aria-hidden="true"
                      :class="{ 'fa-spin': isLoading }"
                    ></i>
                    {{ isLoading ? 'Syncing...' : 'Sync prices now' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Help Information -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">
                <i class="fa fa-question-circle text-info" aria-hidden="true"></i> About Stripe Price Sync
              </h5>
              <p>
                Webhooks already mirror product and price <em>edits</em> automatically, but
                <strong>feature (entitlement) attachment changes are not covered by any webhook</strong>.
                After attaching or detaching a product feature in Stripe, run this to bring the mirror
                back in sync. This function:
              </p>
              <ul>
                <li>Runs the full-catalog mirror against the live Stripe account</li>
                <li>Upserts every product, price and feature into the Postgres mirror tables</li>
                <li>Is safe to click repeatedly (idempotent upsert)</li>
              </ul>
              <div class="alert alert-warning">
                <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>
                <strong>Note:</strong> The sync runs synchronously and may take up to a minute for the
                full catalog. Wait for the success message. If another sync is already running you will
                be asked to try again in a moment.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Config from '@/config'
import utils from '@/utils'

export default {
  name: 'StripePriceSync',
  data() {
    return {
      isLoading: false,
      lastResult: null,
      lastSyncTime: null,
    }
  },
  methods: {
    async triggerStripeSync() {
      if (this.isLoading) return

      this.isLoading = true

      try {
        // Admin JWT is attached globally from the ahaTokenAdmin cookie (see router.js).
        // The endpoint runs the mirror inline and returns { ok, productsN, pricesN }.
        const res = await this.$http.post(Config.api.syncStripeMirror)

        this.lastResult = res.body
        this.lastSyncTime = utils.isoDateTime(new Date())
        utils.showToastSuccess(
          `Synced ${res.body.productsN} products and ${res.body.pricesN} prices into the mirror.`,
        )
      } catch (error) {
        // 409 = a sync is already in progress; anything else = generic failure.
        if (error.status === 409) {
          utils.showToastError('A sync is already running. Give it a moment and try again.')
        } else {
          const errorMessage =
            (error.body && (error.body.error || error.body.message)) ||
            error.bodyText ||
            'Failed to sync Stripe prices. Please try again.'
          utils.showToastError(errorMessage)
        }
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-lg {
  padding: 12px 24px;
  font-size: 16px;
}

.alert {
  border-radius: 8px;
}

.card-title {
  color: #2c3e50;
  font-weight: 600;
}

.card-text {
  color: #6c757d;
  font-size: 14px;
}
</style>
